import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { IconUserPlus, IconTrash, IconUsers } from '@tabler/icons-react';
import {
  useGetProjectIdMembers,
  usePostProjectIdMembers,
  useDeleteProjectIdMembersUserId,
  getGetProjectIdMembersQueryKey,
} from '../../api/project-api/project-api';
import { useNotifications } from '../../hooks/useNotifications';
import { useCurrentUserPermissions } from '../../hooks/useCurrentUserPermissions';
import { useTenantUsers, userDisplayName } from '../../hooks/useTenantUsers';
import { PermissionKey, ProjectMemberDto } from '../../api/model';
import UserPicker from '../ui/UserPicker/UserPicker';
import ConfirmationDialog from '../ui/ConfirmationDialog/ConfirmationDialog';
import styles from './ProjectMembers.module.scss';

interface ProjectMembersProps {
  projectId: number;
}

// Avatar chip for first letters of name and lastname
function initials(first?: string, last?: string, username?: string): string {
  const combined = `${(first?.[0] || '').toUpperCase()}${(last?.[0] || '').toUpperCase()}`.trim();
  return combined || (username?.[0] || '?').toUpperCase();
}

// Lists, adds and removes members of a project
const ProjectMembers: React.FC<ProjectMembersProps> = ({ projectId }) => {
  const { t } = useTranslation('project');
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotifications();
  const { can } = useCurrentUserPermissions();
  const { users: allUsers } = useTenantUsers();

  const canManageMembers = can(PermissionKey.EDIT_PROJECT_STRUCTURE, 'UPDATE');

  const [pendingUserId, setPendingUserId] = useState<number | undefined>(undefined);
  // member staged for removal
  const [pendingRemoval, setPendingRemoval] = useState<ProjectMemberDto | null>(null);

  const { data: members, isLoading } = useGetProjectIdMembers(projectId, {
    query: { enabled: !!projectId, staleTime: 60 * 1000 },
  });
  const addMember = usePostProjectIdMembers();
  const removeMember = useDeleteProjectIdMembersUserId();

  const memberIds = useMemo(
    () => new Set((members ?? []).map(m => m.userId)),
    [members]
  );

  // only offer users that are not already members
  const assignableUsers = useMemo(
    () => allUsers.filter(u => u.id != null && !memberIds.has(u.id)),
    [allUsers, memberIds]
  );

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getGetProjectIdMembersQueryKey(projectId) });

  const handleAdd = () => {
    if (!pendingUserId) return;
    addMember.mutate(
      { id: projectId, params: { userId: pendingUserId } },
      {
        onSuccess: () => {
          showSuccess(t('members.feedback.added', 'Member added'));
          setPendingUserId(undefined);
          refresh();
        },
        onError: () => showError(t('members.feedback.addFailed', 'Could not add member.')),
      }
    );
  };

  const confirmRemove = () => {
    const target = pendingRemoval;
    if (!target?.userId) return;
    removeMember.mutate(
      { id: projectId, userId: target.userId },
      {
        onSuccess: () => {
          showSuccess(t('members.feedback.removed', 'Member removed'));
          refresh();
        },
        onError: () => showError(t('members.feedback.removeFailed', 'Could not remove member.')),
      }
    );
    setPendingRemoval(null);
  };

  return (
    <section className={styles.members}>
      <div className={styles.sectionHead}>
        <h2 className={styles.heading}>
          <IconUsers size={20} />
          {t('members.title', 'Project Team')}
        </h2>
        <p className={styles.subtitle}>
          {t('members.subtitle', 'Members can access this project according to their role. Project-scoped roles only see projects they belong to.')}
        </p>
      </div>

      {canManageMembers && (
        <div className={styles.addRow}>
          <div className={styles.addPicker}>
            <UserPicker
              value={pendingUserId}
              onChange={setPendingUserId}
              users={assignableUsers}
              allowClear={false}
              placeholder={t('members.addPlaceholder', 'Select a person to add…')}
            />
          </div>
          <button
            type="button"
            className={styles.addButton}
            onClick={handleAdd}
            disabled={!pendingUserId || addMember.isPending}
          >
            <IconUserPlus size={16} />
            {addMember.isPending ? t('members.adding', 'Adding…') : t('members.add', 'Add member')}
          </button>
        </div>
      )}

      <div className={styles.list}>
        {isLoading ? (
          <div className={styles.hint}>{t('members.loading', 'Loading members…')}</div>
        ) : (members ?? []).length === 0 ? (
          <div className={styles.hint}>{t('members.empty', 'No members yet.')}</div>
        ) : (
          (members ?? []).map(m => (
            <div key={m.id ?? m.userId} className={styles.memberRow}>
              <span className={styles.avatar}>{initials(m.firstName, m.lastName, m.username)}</span>
              <div className={styles.memberMain}>
                <span className={styles.memberName}>
                  {userDisplayName({ firstName: m.firstName, lastName: m.lastName, username: m.username, id: m.userId })}
                </span>
                <span className={styles.memberMeta}>@{m.username}</span>
              </div>
              {m.tenantRoleDisplayName && (
                <span className={styles.roleBadge}>{m.tenantRoleDisplayName}</span>
              )}
              {canManageMembers && (
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => setPendingRemoval(m)}
                  disabled={removeMember.isPending}
                  aria-label={t('members.remove', 'Remove member')}
                  title={t('members.remove', 'Remove member')}
                >
                  <IconTrash size={16} />
                </button>
              )}
            </div>
          ))
        )}
      </div>

      <ConfirmationDialog
        isOpen={!!pendingRemoval}
        onClose={() => setPendingRemoval(null)}
        onConfirm={confirmRemove}
        type="danger"
        title={t('members.confirm.removeTitle', 'Remove member?')}
        message={t('members.confirm.removeMessage', {
          name: pendingRemoval
            ? userDisplayName({
                firstName: pendingRemoval.firstName,
                lastName: pendingRemoval.lastName,
                username: pendingRemoval.username,
                id: pendingRemoval.userId,
              })
            : '',
          defaultValue: 'Remove {{name}} from this project? They will lose access unless their role is tenant-wide.',
        })}
        confirmText={t('members.confirm.removeConfirm', 'Remove')}
        cancelText={t('members.confirm.cancel', 'Cancel')}
      />
    </section>
  );
};

export default ProjectMembers;
